<template lang="pug">
  div
    .modal-cover(
      v-if="downloading"
      @click.stop.prevent="onDownloading ? null : close"
    )
    .common-modal(:class="{ open: downloading }")
      button.common-modal-close(
        type="button"
        :disabled="onDownloading"
        @click.stop.prevent="close"
      ) 閉じる

      .common-modal-title
        template(v-if="isSearchJob") 検索結果をCSV出力
        template(v-else) {{ titleLabel }}情報のダウンロードを開始します
      .common-modal-main
        .common-modal-main-inner
          template(v-if="isSearchJob")
            p 現在の検索条件・並び順でCSVを作成します。
            p
              | 対象: 検索結果 {{ searchJobCsvTotalCount.toLocaleString() }}件のうち
              | {{ searchJobCsvExportCount.toLocaleString() }}件を出力します。
          template(v-else)
            p
              template(v-if="!isAggregation")
                | 現在、{{ selectedCount }}件の{{ titleLabel }}情報を選択しています。
                br
              | CSV形式でダウンロードします。
            p
              | ※選択している件数が多い場合、ダウンロードに時間がかかる可能性があります。

          p(v-if="showAllColumns")
            label.checkbox
              input(type="checkbox", v-model="allColumns" :disabled="onDownloading")
              span すべての項目を含める

          p.common-modal-button(v-if="onDownloading")
            input.button-main(type="button", value="ダウンロード中..." disabled)
          p.common-modal-button(v-else)
            input.button-main(type="button", :value="startLabel" @click="start")
    .modal-cover(
      v-if="downloading"
      @click.stop.prevent="onDownloading ? null : close"
    )
</template>

<script>
import { mapGetters } from 'vuex'
import { HumanErrorMessages } from '../../constants'

export default {
  data () {
    return {
      allColumns: false,
    }
  },
  computed: {
    ...mapGetters({
      downloading: 'common/downloading',
      onDownloading: 'common/onDownloading',
      csvTarget: 'common/csvTarget',
      modalMode: 'common/modalMode',
      searchJobCsv: 'common/searchJobCsv',
      checkedJobIds: 'job/checkedIds',
      checkedJobCount: 'job/checkedCount',
      checkedJobListIds: 'jobList/checkedIds',
      checkedJobListCount: 'jobList/checkedCount',
      checkedCompanyIds: 'company/checkedIds',
      checkedCompanyCount: 'company/checkedCount',
      mapCompanyIds: 'map/companyIds',
      mapCompanyCount: 'map/companyIdsCount',
      aggregationJobCount: 'aggregateJob/checkedCount',
    }),

    isAggregation () {
      return this.modalMode === 4
    },

    isSearchJob () {
      return this.modalMode === 5
    },

    target () {
      if (this.isAggregation) return null
      return this.csvTarget // 'job' | 'job_list' | 'company' | 'company_list'
    },

    titleLabel () {
      if (this.isAggregation) return '集計'
      if (this.target === 'company' || this.target === 'company_list') return '企業'
      if (this.target === 'job' || this.target === 'job_list') return '求人'
      return ''
    },

    showAllColumns () {
      return this.isSearchJob || this.isAggregation || this.target === 'job' || this.target === 'job_list'
    },

    searchJobCsvTotalCount () {
      return Number((this.searchJobCsv && this.searchJobCsv.totalCount) || 0)
    },

    searchJobCsvExportCount () {
      return Math.min(this.searchJobCsvTotalCount, 10000)
    },

    startLabel () {
      return this.isSearchJob ? 'CSV作成を開始' : '開始'
    },

    mapCompanyCountResolved () {
      const countFromGetter = Number.isFinite(this.mapCompanyCount) ? this.mapCompanyCount : 0
      const countFromIds = this.normalizeIds(this.mapCompanyIds).length
      return Math.max(countFromGetter, countFromIds)
    },

    selectedCount () {
      if (this.isAggregation) return this.aggregationJobCount || 0

      if (this.target === 'job') return this.checkedJobCount
      if (this.target === 'job_list') return this.checkedJobListCount

      if (this.target === 'company' || this.target === 'company_list') {
        return this.modalMode === 3 ? this.mapCompanyCountResolved : this.checkedCompanyCount
      }
      return 0
    },
  },
  watch: {
    $route () {
      if (this.onDownloading) return
      this.$store.commit('common/hideModal')
    },
    searchJobCsv () {
      this.allColumns = false
    },
  },
  methods: {
    close () {
      if (this.onDownloading) return
      this.$store.commit('common/hideModal')
      // eslint-disable-next-line no-console
      if (this.onDownloading) return
      this.$store.commit('common/hideModal')
    },

    sleep (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    waitUntilUploaded (target, { intervalMs = 3000, timeoutMs = 10 * 60 * 1000 } = {}) {
      const startedAt = Date.now()

      const poll = async () => {
        if (Date.now() - startedAt > timeoutMs) {
          throw new Error('データ作成がタイムアウトしました。時間を置いて再度お試しください')
        }

        const latest = await this.$store.dispatch('uploadArchive/fetchLatest', { target })
        const status = latest && latest.status

        if (status === 'uploaded') return latest

        if (status === 'failed') {
          const msg = latest && latest.error_message
            ? latest.error_message
            : 'データ作成に失敗しました'
          throw new Error(msg)
        }

        await this.sleep(intervalMs)
        return poll() // ★再帰
      }

      return poll()
    },

    // MySet / Set / Array / iterable すべてに対応して配列化
    normalizeIds (ids) {
      if (!ids) return []
      if (Array.isArray(ids)) return ids

      const arr = []

      if (ids && typeof ids.forEach === 'function') {
        ids.forEach((v) => { arr.push(v) })
        return arr
      }

      try {
        if (ids && typeof ids.values === 'function') return Array.from(ids.values())
        // eslint-disable-next-line no-empty
      } catch (e) {}

      try {
        if (typeof ids[Symbol.iterator] === 'function') return Array.from(ids)
        // eslint-disable-next-line no-empty
      } catch (e) {}

      return []
    },

    getSelectedIds () {
      if (this.target === 'job') return this.normalizeIds(this.checkedJobIds)
      if (this.target === 'job_list') return this.normalizeIds(this.checkedJobListIds)

      if (this.target === 'company' || this.target === 'company_list') {
        return (this.modalMode === 3)
          ? this.normalizeIds(this.mapCompanyIds)
          : this.normalizeIds(this.checkedCompanyIds)
      }
      return []
    },

    buildPayload (ids) {
      const payload = {
        target: this.target,
        all_columns: !!this.allColumns,
      }
      if (this.target === 'company' || this.target === 'company_list') {
        payload.company_ids = ids.join(',')
      } else {
        payload.job_ids = ids.join(',')
      }
      const sortContext = this.buildSortContext()
      if (sortContext) payload.sort_context = sortContext
      return payload
    },

    buildSortContext () {
      // 地図は画面に固定の全件ソートがないため既存動作を維持する
      if (this.modalMode === 3) return null

      if (this.target === 'job') {
        const query = this.$store.state.searchJob.query
        return {
          version: 1,
          order: query.order || '',
          direction: query.orderDirection || '',
          // 給与ソートは給与種別が無いと並びを再現できない（Rails 側で検証する）。
          // Vuex ではなく route query を見る。Vuex 側はサイドバーでラジオを
          // 選んだ時点で書き換わり、画面に出ている一覧と食い違うため。
          salary_cd: this.$route.query.salaryCd || '',
        }
      }
      if (this.target === 'company') {
        const query = this.$store.state.searchCompany.query
        return {
          version: 1,
          order: query.order || '',
          station_id: query.stationId || '',
        }
      }
      if (this.target === 'job_list') {
        return {
          version: 1,
          order: this.$store.state.searchJobList.query.order || '',
        }
      }
      if (this.target === 'company_list') return { version: 1, order: '' }
      return null
    },

    handleError (e) {
      if (e && e.response && e.response.status === 403) {
        this.$store.dispatch('common/pushToastError', {
          error: new Error(HumanErrorMessages.FORBIDDEN),
        })
      } else if (e && e.response && e.response.data && e.response.data.error) {
        this.$store.dispatch('common/pushToastError', {
          error: new Error(e.response.data.error),
        })
      } else {
        this.$store.dispatch('common/pushToastError', { error: e })
      }
    },

    async start () {
      if (this.onDownloading) return

      this.$store.commit('common/startDownload')
      const shouldClose = true
      try {
        if (this.isAggregation) {
          await this.$store.dispatch('aggregateJob/downloadAggregationJobCsv', { allColumns: this.allColumns })
          return
        }

        if (this.isSearchJob) {
          const search = this.searchJobCsv && this.searchJobCsv.search
          if (!search) throw new Error('検索条件が取得できませんでした')

          const { archive } = await this.$store.dispatch('uploadArchive/createSearchJob', {
            all_columns: !!this.allColumns,
            search,
          })
          if (!archive || !archive.id) throw new Error('データ作成の開始に失敗しました')

          await this.waitUntilUploaded(this.target)
          await this.$store.dispatch('uploadArchive/download', { target: this.target, id: archive.id })
          return
        }

        const ids = this.getSelectedIds()
        if (!ids || ids.length === 0) {
          if (this.modalMode === 3) {
            throw new Error('ダウンロード対象となる企業がありません')
          }
          throw new Error('ダウンロード対象となるデータが指定されていません')
        }

        const payload = this.buildPayload(ids)

        // ①作成開始
        await this.$store.dispatch('uploadArchive/create', payload)

        // ②uploaded になるまで待つ
        await this.waitUntilUploaded(this.target)

        // ③自動でダウンロード
        await this.$store.dispatch('uploadArchive/download', { target: this.target })
      } catch (e) {
        this.handleError(e)
      } finally {
        // ④成功/失敗どちらでも「開始後は最後に閉じる」
        this.$store.commit('common/endDownload')
        if (shouldClose) this.$store.commit('common/hideModal')
      }
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/parts/CommonModal.vue